import { TUNING } from '~/config/tuning'
import { CREATURE_POOL, getCreatureDef } from '~/data/creatures'
import { ELEMENTS, type CreatureDef, type Mission, type Stats } from '~/types/game'
import { emptyStats } from '~/utils/battle'
import { makeId } from '~/utils/random'

/**
 * 🧩 Puzzle Game (v1.28, per Javi): a standalone gauntlet of
 * PUZZLE_LEVEL_COUNT fixed levels (25 at birth, 50 since v3.74),
 * separate from the expedition. Every level is FIXED — the same party
 * and the same trials for every player — generated from a per-level
 * seed, so "level 12" means the same thing on everyone's leaderboard.
 *
 * Each level is a pure logic puzzle: the trials are EXACT stat mirrors
 * of a hidden partition of the party, so a 100% no-damage clear always
 * exists. Difficulty ramps by party size (3 → 7) and trial count — the
 * bigger the party, the more ways to split it wrong.
 */
// In shared/ since v4.02 — the leaderboard route scores against it too.
export { PUZZLE_LEVEL_COUNT } from '../../shared/puzzleMeta'
import { PUZZLE_LEVEL_COUNT } from '../../shared/puzzleMeta'

/** Deterministic PRNG (mulberry32) — the whole level flows from this. */
function seeded(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pick<T>(rng: () => number, list: T[]): T {
  return list[Math.floor(rng() * list.length)]!
}

function shuffled<T>(rng: () => number, list: T[]): T[] {
  const out = [...list]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

const TRIAL_NAMES = [
  'Trial of Echoes', 'Trial of Mirrors', 'Trial of Shadows',
  'Trial of Whispers', 'Trial of Embers', 'Trial of Tides',
]

/** Party ramp (v1.34, per Javi — "level 15 should really be level 5"):
 * +1 creature per level, capped at 10 by level 8. Past that the OTHER
 * axes keep climbing (scramble depth, surge trials, group sizes).
 *
 * v3.74 lifts the cap again for the 26–50 stretch, one body per five
 * levels to a ceiling of 14. Party size is the sharpest axis available:
 * solving a level is a set-partition search, so each extra creature
 * multiplies the ways to be wrong far faster than another point of
 * stat scramble does. Levels 1–25 are untouched by design — their
 * numbers must not move under players who have already scored them. */
export function puzzlePartySize(level: number): number {
  if (level <= 25) return Math.min(10, 2 + level)
  return Math.min(14, 10 + Math.ceil((level - 25) / 6))
}

/** A party slot with its (possibly scrambled) stats for this level. */
export interface PuzzlePartyMember {
  defId: string
  name: string
  speed: number
  fight: number
  stats: Stats
}

export interface PuzzleLevelDef {
  level: number
  party: PuzzlePartyMember[]
  /** Field-agnostic trials; the session places them on the forest. */
  missions: Mission[]
}

/**
 * Builds level N from its fixed seed. Mission ids are random (they only
 * key the UI); everything gameplay-relevant is seed-stable.
 */
export function buildPuzzleLevel(level: number): PuzzleLevelDef {
  const rng = seeded(0xED0 + level * 7919)
  const size = puzzlePartySize(level)
  // Common pool only, id-sorted so the roll never shifts as the pool
  // reorders; rares keep their shop exclusivity.
  const pool = [...CREATURE_POOL]
    .filter(def => def.rarity !== 'rare')
    .sort((a, b) => a.id.localeCompare(b.id))
  // 🌶️ Axis 2 (compressed in v1.34): themed parties from level 2,
  // all-in from level 3 — members share stat profiles and look
  // interchangeable almost immediately.
  const theme = level >= 2 ? shuffled(rng, [...ELEMENTS]).slice(0, 2) : []
  const themedPool = pool.filter((def) => {
    const max = Math.max(...ELEMENTS.map(e => def.stats[e]))
    return theme.some(e => def.stats[e] === max)
  })
  const party: CreatureDef[] = []
  while (party.length < size) {
    const useTheme = theme.length > 0
      && (level >= 3 || rng() < 0.6)
      && themedPool.some(d => !party.includes(d) && !d.awardOnly)
    const def = pick(rng, useTheme ? themedPool : pool)
    // 🙈 v7.81 (per Javi: "standalone puzzle game get rid of any homage creature"): a hidden (award-only) creature is
    // thrown back and the draw goes again - a level that never drew one deals exactly as before; only those that did
    // are re-dealt. (The pool keeps them, so no other level's draws shift.)
    if (def.awardOnly) continue
    if (!party.includes(def)) party.push(def)
  }

  // 🌶️ Axis 4 (compressed in v1.34): from level 5 every member's stats
  // are SCRAMBLED with seeded bonus points, deepening every 2 levels —
  // you can't eyeball a Dew Hare and know its line; read the numbers.
  const bonus = level >= 5 ? 2 + Math.floor((level - 5) / 2) : 0
  const members: PuzzlePartyMember[] = party.map((def) => {
    const stats: Stats = { ...def.stats }
    for (let b = 0; b < bonus; b++) stats[pick(rng, [...ELEMENTS])]++
    return { defId: def.id, name: def.name, speed: def.speed, fight: def.fight ?? 0, stats }
  })

  // Hidden answer: split the party into groups; each trial mirrors one
  // group's stat total EXACTLY (no excess → a perfect clear exists).
  // 🌶️ Axis 5 (v1.33, compressed v1.34): NO singleton trials from
  // level 2 — a trial that mirrors one creature solves itself. Groups
  // run 2–4 (3–4 from level 12), uneven from level 3, so every mission
  // needs a real team.
  // v3.74: past 30 a trial can swallow five, which lets group sizes go
  // uneven in more ways. A matching minGroup of 4 was tried and REMOVED:
  // fatter groups mean FEWER of them, and measured against the number of
  // distinct assignments a solver must consider it made level 40 easier
  // than 35 (90,090 vs 369,600). Group count is worth more than group
  // size here, so the floor stays at 3.
  const minGroup = level >= 12 ? 3 : level >= 2 ? 2 : 1
  const maxGroup = level >= 30 ? 5 : 4
  let groupCount = Math.max(2, Math.round(size / 3))
  groupCount = Math.max(groupCount, Math.ceil(size / maxGroup))
  groupCount = Math.min(groupCount, Math.floor(size / minGroup))
  const order = shuffled(rng, members.map((_, i) => i))
  const sizes = Array.from({ length: groupCount }, () => minGroup)
  let left = size - groupCount * minGroup
  while (left > 0) {
    const gi = level >= 3 ? Math.floor(rng() * groupCount) : left % groupCount
    if (sizes[gi]! < maxGroup) {
      sizes[gi]!++
      left--
    }
  }
  const groups: number[][] = []
  let cursor = 0
  for (const s of sizes) {
    groups.push(order.slice(cursor, cursor + s))
    cursor += s
  }

  const missions = groups.map((group, index) => {
    const required: Stats = emptyStats()
    for (const idx of group) {
      const member = members[idx]!
      for (const element of ELEMENTS) required[element] += member.stats[element]
    }
    // 🌶️ Axis 6 (v1.34, per Javi): from level 10, ~half the trials ask
    // for MORE than the group's raw total — exactly one member's Surge
    // worth. Priming the right creature's surge is part of the answer;
    // the ⚡ in the name says so up front. Members with a tied top stat
    // are skipped (dual-surge math stays out of the mirror).
    let surge = false
    // v3.74: surge trials get commoner in the back half — more of the
    // answer lives in "who primes what" rather than "who goes where".
    if (level >= 10 && rng() < (level >= 26 ? 0.65 : 0.5)) {
      // ⚠️ The surge element comes from the creature's BASE definition,
      // NOT its scrambled puzzle statline — that is what `creatureSkill`
      // reads in battle.ts, so it is the only element the player can
      // actually add +2 to. Reading the scrambled stats here (v1.34 →
      // v3.73) made the mirror ask for a surge nobody could cast the
      // moment scrambling moved a creature's top stat: 9 of the 25
      // levels had NO perfect clear, against the promise at the top of
      // this file. Javi caught it on 23 — Solar Kirin scrambles to
      // grass-topped but is fire by definition, so its surge is +2 fire.
      const candidates = group.filter((idx) => {
        const def = getCreatureDef(members[idx]!.defId)
        // 🚫 v3.89 (Javi's puzzle 16: "the surge gives earth when it
        // needs water"): skill-carrying creatures are noSurge — they have
        // NO surge button — but this filter only checked their statline,
        // so a mirror could be built around a surge nobody can cast.
        // Marrowlight Jelly (base top: water, noSurge) carried level 16's
        // Echoes trial; the only real surgers on that team give earth.
        if (def.noSurge) return false
        const base = def.stats
        const max = Math.max(...ELEMENTS.map(e => base[e]))
        // A tie means a DUAL surge, which pays a different bonus — keep
        // that maths out of the mirror, as the original intent had it.
        return max > 0 && ELEMENTS.filter(e => base[e] === max).length === 1
      })
      if (candidates.length > 0) {
        const base = getCreatureDef(members[pick(rng, candidates)]!.defId).stats
        const top = ELEMENTS.reduce((a, b) => (base[b] > base[a] ? b : a))
        required[top] += TUNING.skills.surgeBonus
        surge = true
      }
    }
    const fight = 5 + Math.floor(rng() * 4)
    // Misses hurt properly now: ~10 at level 4, ~30 by level 25.
    const damage = 5 + Math.floor(rng() * 4) + level
    return {
      id: makeId('pz'),
      name: `${TRIAL_NAMES[index] ?? `Trial ${index + 1}`}${surge ? ' ⚡' : ''}`,
      tier: 'medium',
      puzzle: true,
      x: 50,
      y: 100,
      ctrlX: 50,
      ctrlY: 250,
      distance: 6,
      travelRemaining: 0,
      travelTotal: 0,
      required,
      expireTimer: 9999,
      expireTotal: 9999,
      fightTimer: fight,
      fightTotal: fight,
      damage,
      xp: 0,
      // 🌶️ Axis 3 (compressed in v1.34): from level 4 the slots are
      // TIGHT (exactly the hidden group's size) — no padding a trial
      // with an extra body; the split must be found.
      slots: level >= 4 ? Math.max(1, group.length) : Math.min(5, group.length + 1),
      puzzleAnswer: [],
      assignedCreatureIds: [],
      assignedChance: null,
      status: 'waiting',
      removeIn: null,
    } as Mission
  })

  return { level, party: members, missions }
}

/** Sanity used by tests/build checks: the mirrored partition solves
 * every trial with room to spare (slots fit the answer group). */
export function levelIsSolvable(def: PuzzleLevelDef): boolean {
  return def.missions.every(m =>
    ELEMENTS.every(e => m.required[e] >= 0) && m.slots >= 1)
}

/** Overall score, per Javi: PUZZLE_LEVEL_COUNT − highest completed level
 * + total best damage across completed levels. 0 is a perfect full
 * clear; LOWEST wins. NOTE the count is part of the formula, so raising
 * it (25 → 50 in v3.74) shifts every existing score by the difference
 * until those players climb the new levels. */
export function puzzleTotalScore(best: Record<number, number>): { highest: number; damage: number; score: number } {
  let highest = 0
  let damage = 0
  for (let lv = 1; lv <= PUZZLE_LEVEL_COUNT; lv++) {
    if (best[lv] === undefined) break
    highest = lv
    damage += best[lv]!
  }
  return { highest, damage, score: PUZZLE_LEVEL_COUNT - highest + damage }
}

/** The def for a party index list — kept tiny; sessions build real
 * creatures from defs. */
export function puzzleCreatureDef(defId: string): CreatureDef {
  return getCreatureDef(defId)
}
